import { defineStore, storeToRefs } from 'pinia';
import { AvatarOptions } from '@onereach/hitl-base-avatars';

import { HITL } from '@/api';
import {
  SettingsObj,
  CustomStylesData,
  HeaderConfig,
  TimelineConfig,
} from '@/types';
import { useFilesApi } from '@/composables';
import { LAYOUT_VERSION } from '@/modules/layout-builder/constants';
import {
  SETTINGS_CATEGORIES,
  DEFAULT_AVATAR_SETTINGS,
  DEFAULT_CUSTOM_AVATAR,
} from '@/constants';
import { UIEventBus, THROW_API_ERROR } from '@/common/ui-event-bus';

export const LOGO_FILE_UPLOAD_DIR_NAME = 'HitL';
export const useAppearanceSettingsStore = defineStore('HITL-Settings-Appearance', {
  state: () => {
    return {
      isReady: false,
      isUpdating: false,
      avatar: DEFAULT_AVATAR_SETTINGS as AvatarOptions,
      styles: { sass: '', css: '' } as CustomStylesData,
      header: {} as HeaderConfig,
      timeline: {} as TimelineConfig,
    };
  },
});

export const useAppearanceSettingsActions = () => {
  const store = useAppearanceSettingsStore();
  const { uploadFile } = useFilesApi();

  const initializeAppearanceSettings = async (options: { isCommon?: boolean; } = {}) => {
    try {
      if (store.isReady) {
        return;
      }

      store.isReady = false;
      const categories = options.isCommon
        ? [SETTINGS_CATEGORIES.STYLES, SETTINGS_CATEGORIES.LAYOUT]
        : [];

      const response = await HITL.settings.getSettings({
        isCommon: !!options.isCommon,
        categories: [...categories, SETTINGS_CATEGORIES.AVATAR],
      });

      const avatar = response.data[SETTINGS_CATEGORIES.AVATAR] as SettingsObj;
      if (avatar) {
        store.avatar = migrateAvatar(avatar.data);
      }

      const styles = response.data[SETTINGS_CATEGORIES.STYLES] as SettingsObj;
      if (styles) {
        store.styles = styles.data;
      }

      const layout = response.data[SETTINGS_CATEGORIES.LAYOUT] as SettingsObj;
      if (layout) {
        store.header = layout.data.header as HeaderConfig;
        store.timeline = layout.data.timeline as TimelineConfig;
      }

      store.isReady = true;
    } catch (error) {
      console.error('Error while initializing appearance settings!', error);
      UIEventBus.$emit(THROW_API_ERROR, { error });
    }
  };

  const updateAppearanceSettings = async (data: {
    avatar?: AvatarOptions;
    styles?: CustomStylesData;
    header?: HeaderConfig;
    timeline?: TimelineConfig;
  }) => {
    if (data.avatar) {
      store.avatar = data.avatar;
    }
    if (data.styles) {
      store.styles = data.styles;
    }
    if (data.header) {
      store.header = data.header;
    }
    if (data.timeline) {
      store.timeline = data.timeline;
    }
  };

  const updateAPIAppearanceSettings = async (options: { isCommon?: boolean; } = {}) => {
    try {
      store.isUpdating = true;

      /* Upload logo image file before saving the settings if provided */
      const { imageLogo } = store.header;
      if (imageLogo instanceof File) {
        const path = LOGO_FILE_UPLOAD_DIR_NAME;
        const imageUrl = (await uploadFile(imageLogo, path)) as string;
        store.header = {
          ...store.header,
          imageLogo: imageUrl,
        };
      }

      if (store.header.title) {
        store.header = {
          ...store.header,
          title: store.header.title.trim(),
        };
      }

      const common = {} as Record<string, object>;
      if (options.isCommon) {
        Object.assign(common, {
          [SETTINGS_CATEGORIES.STYLES]: store.styles,
          [SETTINGS_CATEGORIES.LAYOUT]: {
            header: store.header,
            timeline: store.timeline,
            version: LAYOUT_VERSION,
          },
        });
      }

      await HITL.settings.updateSettings({
        isCommon: !!options.isCommon,
        data: {
          ...common,
          [SETTINGS_CATEGORIES.AVATAR]: store.avatar,
        },
      });
      store.isUpdating = false;
    } catch (error) {
      store.isUpdating = false;
      console.error('Error while updating appearance settings!', error);
      UIEventBus.$emit(THROW_API_ERROR, { error });
    }
  };

  const migrateAvatar = (item: AvatarOptions) => {
    const copy = { ...item };
    if (!item.custom) {
      /* TODO: Remove "gravatar" property in the future */
      copy.custom = {
        isEnabled: false,
        default: DEFAULT_CUSTOM_AVATAR,
      };
    }
    return copy;
  };

  return {
    migrateAvatar,
    initializeAppearanceSettings,
    updateAppearanceSettings,
    updateAPIAppearanceSettings,
  };
};

export const useAppearanceSettings = () => {
  return {
    ...storeToRefs(useAppearanceSettingsStore()),
    ...useAppearanceSettingsActions(),
  };
};
